import { Fragment, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Container, HooksService, Store } from 'basic-components';
import { DispatcherEnums } from 'dashboard-services/api/dispatcher';
import Query from 'dashboard-services/api/Query';
import { CommonService } from 'dashboard-services/services';
import { apiConfigActions, AppHeader, Fetching, globalActions, loginActions, userAuthQueries } from 'primary-components';

import { settingsMyAccountActions } from 'actions/modules/myAccount';
import getApiConfig from 'api/ApiConfig';
import { dispatcherQueries } from 'queries/modules/dispatcher';
import { allBreadcrumbs } from 'resources/constants/allBreadcrumbs';
import properties from 'resources/constants/properties.json';
import { routing } from 'routing';
import { customInsightSelectors } from 'selectors/modules/insight/custom';
import { sendEvent } from 'services/GAService';
import { clearLocalStorage, getRefreshToken, REFRESH_TOKEN_SOURCES, setRefreshToken } from 'services/LocalStorageService';

import ErrorBoundary from 'components/App/ErrorBoundary';

import DesktopRouting from './DesktopRouting';
import EndOfService from './EndOfService';
import MobileRouting from './MobileRouting';
import NonAuthedRouting from './NonAuthedRouting';
import PublicRouting from './PublicRouting';
import StatusBox from './StatusBox';

import { useQueryClient } from '@tanstack/react-query';
import { deviceType } from 'react-device-detect';
import ReactGA from 'react-ga4';
import { Else, If, Then, When } from 'react-if';

import './App.scss';

const MessageStore = new Store()
const ToastsStore = new Store()
const { useIsPublicDirectory } = customInsightSelectors
const MCP_SEARCH_PARAM = "mcp"

const getOnLineStatus = () =>
  typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
      ? navigator.onLine
      : true;
const sha256 = message => {
        const msgBuffer = new TextEncoder().encode(message);                    
        return crypto.subtle.digest('SHA-256', msgBuffer).then(hashBuffer => {
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
          return hashHex;
        })
      },
      gaOptionsCommon = {
        // "debug_mode": true, // Needs chrome plugin
      },
      gtagOptionsCommon = {
        // "debug_mode": true,
      }

const useNetworkStatus = () => {
  const dispatch = useDispatch(),
        prevOnline = useRef(true),
        updateNetworkStatus = useCallback(() => {
          const isOnline = getOnLineStatus()
          if(!isOnline) {
            MessageStore.info("You are offline.", undefined, { timer: 30_000 })
          } else if(prevOnline.current === false && isOnline) {
            MessageStore.info("You are back online.", undefined, { timer: 30_000 })
          }
          dispatch(globalActions.onChange(!isOnline, "isOffline"))
          prevOnline.current = isOnline
        }, [dispatch, prevOnline])
  useEffect(() => {
    window.addEventListener("online", updateNetworkStatus);
    window.addEventListener("offline", updateNetworkStatus);

    return () => {
        window.removeEventListener("online", updateNetworkStatus);
        window.removeEventListener("offline", updateNetworkStatus);
    };
  }, [updateNetworkStatus]);
}

const useLoadSettings = ({ token }) => {
  const dispatch = useDispatch(),
        navigate = useNavigate(),
        userName = useSelector(state => state.myAccountState.settingsState.userName),
        isPublic = useIsPublicDirectory(),
        getQueryBuilderNoError = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withShouldDisplayError(false)
        , [dispatch]),
        { mutateAsync: logOutCall } = userAuthQueries.useLogOutMutation({ getQueryBuilder: getQueryBuilderNoError }),
        getQueryBuilderSettings = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withOnError(e => {
              console.error(e)
              dispatch(loginActions.logOut({ logOutCall, withSideEffects: true }))
            })
            .withFetching(false)
        , [dispatch, logOutCall]),
        { data: settingsResponse } = userAuthQueries.useGetCurrentUserSettings({ token, entitlements: true, getQueryBuilder: getQueryBuilderSettings, enabled: !isPublic && !!token && !userName }),
        prevSettingsResponse = HooksService.usePrevious(settingsResponse)

  useEffect(() => {
    if(settingsResponse && settingsResponse !== prevSettingsResponse) {
      console.info("Loaded user settings response: ", settingsResponse)
      const settingsObject = settingsMyAccountActions.getDefaultSettings(settingsResponse?.settings) || {};
      dispatch(loginActions.setUiEnts(settingsResponse.uiEntitlements))
    
      dispatch(settingsMyAccountActions.changeSetting(CommonService.parseJson(settingsObject.favoritePipelines, []), 'favoritePipelines'))
      dispatch(settingsMyAccountActions.changeSetting(CommonService.parseJson(settingsObject.favoriteGroups, []), 'favoriteGroups'))
      dispatch(settingsMyAccountActions.changeSetting(CommonService.parseJson(settingsObject.favoriteDataPreps, []), 'favoriteDataPreps'))
      dispatch(settingsMyAccountActions.changeSetting(CommonService.parseJson(settingsObject.favoriteInsights, []), 'favoriteInsights'))
      dispatch(settingsMyAccountActions.changeSetting(CommonService.parseJson(settingsObject.defaultPythonPackages, undefined), 'defaultPythonPackages'))
      dispatch(settingsMyAccountActions.changeSetting(CommonService.parseJson(settingsObject.recents, []), 'recents'))
      dispatch(settingsMyAccountActions.changeSetting(CommonService.parseJson(settingsObject.recentTasks, []), 'recentTasks'))
      dispatch(settingsMyAccountActions.changeSetting(CommonService.parseJson(settingsObject.colorPalettes, []), 'colorPalettes'))
      dispatch(settingsMyAccountActions.changeSetting(CommonService.parseJson(settingsObject.formulaPanelConstants, []), 'formulaPanelConstants'))
      dispatch(settingsMyAccountActions.changeSetting(CommonService.parseJson(settingsObject.formulaPanelRecents, []), 'formulaPanelRecents'))

      const filteredSettings = Object.fromEntries(Object.entries(settingsObject).filter(([key, ]) => !['favoritePipelines', 'favoriteGroups', 'favoriteDataPreps', 'favoriteInsights', 'defaultPythonPackages', 'recents', 'recentTasks', 'colorPalettes', 'formulaPanelConstants', 'formulaPanelRecents'].includes(key)))

      dispatch(settingsMyAccountActions.setSettings({
        ...filteredSettings,
        columns: CommonService.parseJson(settingsObject.columns, settingsMyAccountActions.DEFAULT_COLUMNS), 
        symbolsDisplay: CommonService.parseJson(settingsObject.symbolsDisplay, {}),
        storageGroupsHidden: settingsObject.storageGroupsHidden === "true" || settingsObject.storageGroupsHidden === true ? true : false,
        dashboardUserTabs: dispatch(settingsMyAccountActions.getDashboardUserTabs(settingsObject.dashboardUserTabs))
      }))
      const defaultPage = settingsResponse.defaultPage || routing.insight.name
      if(window.location.pathname && (window.location.pathname === "/" || window.location.pathname === "")) {
        navigate('/' + defaultPage, { replace: true })
      }
    }
  }, [dispatch, navigate, prevSettingsResponse, settingsResponse])
}

const useMessageStore = () => {
  const dispatch = useDispatch(),
        MessageStoreState = useSelector(state => state.globalState.MessageStore)
  
  useEffect(() => {
    if(!MessageStoreState) {
      dispatch(globalActions.onChange(MessageStore, "MessageStore"))
      dispatch(globalActions.onChange(ToastsStore, "ToastsStore"))
    }
  }, [dispatch, MessageStoreState])
}

const useGA = () => {
  const userName = useSelector(state => state.myAccountState.settingsState.userName),
        company = useSelector(state => state.myAccountState.settingsState.company),
        isPublic = useIsPublicDirectory(),
        initGA = useCallback(({ gaOptions = {}, gtagOptions = {} } = {}) => {
          ReactGA.initialize(properties.gaId, {
            titleCase: false,
            gaOptions: {
              ...gaOptionsCommon,
              ...gaOptions
            },
            gtagOptions: {
              ...gtagOptionsCommon,
              ...gtagOptions
            },
          })
          console.info("GA is up and running.")
        },[]),
        setUserPropsGA = useCallback(({ properties = {} } = {}) => {
          ReactGA.gtag("set", "user_properties", properties);
          console.info("GA is set up.", Object.values(properties).join(" | "))
        }, [])

  useEffect(() => {
    if(properties.gaId && properties.gaId !== "disabled") {
      if(isPublic){
        initGA()
        setUserPropsGA()
      } else if(userName){
        sha256(userName).then(userId => {
          initGA({ 
            gaOptions: {
              "user_id": userId,
              "org_id": company
            },
            gtagOptions: {}
          })
          setUserPropsGA({ properties: {
              "user_id": userId,
              userName,
              "org_id": company,
              company
            }
          })
        })
      }
    }
  }, [company, initGA, isPublic, setUserPropsGA, userName])
}

const useMcpLoginSearchParam = () => {
  const location = useLocation(),
        navigate = useNavigate(),
        searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]),
        [isMcpLogin, setIsMcpLogin] = useState(() => searchParams.get(MCP_SEARCH_PARAM) === "true")

  useEffect(() => {
    if(searchParams.get(MCP_SEARCH_PARAM) === "true") {
      setIsMcpLogin(true)
      const nextSearchParams = new URLSearchParams(searchParams)
      nextSearchParams.delete(MCP_SEARCH_PARAM)
      navigate({
        hash: location.hash,
        pathname: location.pathname,
        search: nextSearchParams.toString()
      }, { replace: true })
    }
  }, [location.hash, location.pathname, navigate, searchParams])

  return isMcpLogin
}

const useRefreshToken = ({ isMcpLogin }) => {
  const dispatch = useDispatch(),
        isPublic = useIsPublicDirectory(),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withOnError(() => clearLocalStorage())
            .withShouldDisplayError(false)
        , [dispatch]),
        { mutateAsync: onRefreshTokenCall, isPending: isRefreshing } = userAuthQueries.useRefreshTokenMutation({ getQueryBuilder })

  useEffect(() => {
    if(!isPublic && !isMcpLogin) {
      const urlRefreshToken = new URLSearchParams(window.location.search).get("refreshToken")
      const { token: refreshToken, source } = urlRefreshToken ? { token: urlRefreshToken, source: REFRESH_TOKEN_SOURCES.NG } : getRefreshToken()
      if(refreshToken && source === REFRESH_TOKEN_SOURCES.NG) {
        onRefreshTokenCall({ refreshToken })
          .then(response => {
            setRefreshToken({ token: response.refreshToken, source: REFRESH_TOKEN_SOURCES.NG })
            dispatch(loginActions.loginResponse({ token: response.token, source: REFRESH_TOKEN_SOURCES.NG }))
          })
      }
    }
  }, [dispatch, isMcpLogin, isPublic, onRefreshTokenCall])

  return isRefreshing;
}

const useLimits = () => {
  const dispatch = useDispatch(),
        queryClient = useQueryClient(),
        limitedProviders = useSelector(state => state.globalState.limitedProviders),
        prevLimitedProviders = HooksService.usePrevious(limitedProviders),
        disableLimitsTimeout = useRef([])

  useEffect(() => {
    queryClient.setQueryDefaults([], { networkMode: limitedProviders.length > 0 ? "offline-first" : "online" })
  }, [limitedProviders?.length, queryClient])

  useEffect(() => {
    if(prevLimitedProviders?.length < limitedProviders.length && limitedProviders.length > 0) {
      disableLimitsTimeout.current.push(setTimeout(() => {
        dispatch(globalActions.removeLimitedProvider(limitedProviders[limitedProviders.length - 1]))
      }, 1000 * 60 * 2)) // 2 mins
    }
  }, [dispatch, limitedProviders, prevLimitedProviders])

  useEffect(() => () => {
    disableLimitsTimeout.current.forEach(t => clearTimeout(t))
  }, [])
}

const useDispatcherApiAvailability = ({ enabled }) => {
  const dispatch = useDispatch(),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        getQueryBuilder = useCallback(() =>
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
            .withShouldDisplayError(false)
            .withOnSuccess(() => dispatch(globalActions.onChange(true, "isDispatcherApiAvailable")))
            .withOnError(() => dispatch(globalActions.onChange(false, "isDispatcherApiAvailable")))
        ,[dispatch, getApiConfigLocal])
  dispatcherQueries.useGetQueue({ 
    getQueryBuilder,
    queueName: DispatcherEnums.QUEUE_TYPE.TS,
    enabled,
    shouldRefetch: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 0
  })
}

const useDarkMode = () => {
  const darkmode = useSelector(state => state.myAccountState.settingsState.darkmode)

  useEffect(() => {
    const body = document.getElementsByTagName("body")[0],
          localDarkMode = darkmode || settingsMyAccountActions.DARK_MODE.OFF
    if(localDarkMode === settingsMyAccountActions.DARK_MODE.ON) {
      body.classList.remove("color-scheme-light");
      body.classList.remove("color-scheme-auto");
      body.classList.add("color-scheme-dark");
    } else if(localDarkMode === settingsMyAccountActions.DARK_MODE.OFF) {
      body.classList.add("color-scheme-light");
      body.classList.remove("color-scheme-auto");
      body.classList.remove("color-scheme-dark");
    } else if(localDarkMode === settingsMyAccountActions.DARK_MODE.AUTO) {
      body.classList.remove("color-scheme-light");
      body.classList.add("color-scheme-auto");
      body.classList.remove("color-scheme-dark");
    }
  }, [darkmode])
}

const useInitLogging = () => {
  const dispatch = useDispatch(),
        { pathname } = useLocation(),
        isIframe = useMemo(() => window !== window.parent, [])

  useEffect(() => {
    if(isIframe) {
      console.info("Iframe detected.")
    }
  }, [isIframe])

  useEffect(() => {
    if(isIframe) {
      console.info("Iframe detected.")
      const event = {
        category: "view_item",
        action: "open_iframe",
        label: "open_iframe",
      };
      dispatch(sendEvent(event))
    }
  }, [dispatch, isIframe])

  useEffect(() => {
    if(properties.gaId && properties.gaId !== "disabled") {
      ReactGA.send({ hitType: "pageview", page: pathname });
    }
  }, [pathname])
}

const App = () => {
  const dispatch =  useDispatch(),
        isMcpLogin = useMcpLoginSearchParam(),
        isRefreshing = useRefreshToken({ isMcpLogin }),
        isMobile = useSelector(state => state.globalState.isMobile),
        
        token = useSelector(state => state.primaryComponentsState.loginState.token),
        isFetching = useSelector(state => state.globalState.fetchingRequests.filter(f => !f.noFetching).length > 0),
        
        tokenExist = token != null && typeof token != "undefined" && token !== "",
        isPublic = useIsPublicDirectory(),
        isIframe = useMemo(() => window !== window.parent, []),
        hasStatusAccess = useCallback(() => dispatch(loginActions.hasAccessTo(routing.statuses.name)), [dispatch])

  HooksService.useComponentWillMount(() => {
    dispatch(apiConfigActions.changeApiConfig(getApiConfig)) 
  })

  useEffect(() => {
    dispatch(globalActions.onChange(deviceType === "mobile", "isMobile"))
  },[dispatch])

  useMessageStore()

  useLoadSettings({ token })

  useNetworkStatus()

  useGA()

  useInitLogging()

  useDarkMode()

  useLimits()

  useDispatcherApiAvailability({ enabled: tokenExist && !isPublic })

  return (
    <Suspense fallback={<Fetching isFetching/>}>
      <Fetching isFetching={isFetching}/>
      <When condition={hasStatusAccess}>
        {() => <StatusBox/>}
      </When>
      <Container
          globalTimeout={30000}
          position="top-right"
          store={ToastsStore}
          variant="toasts"
      />
      <Container
          globalTimeout={5000}
          isMobile={isMobile}
          position="top-center"
          store={MessageStore}
          variant="message"
      />
      <When condition={properties.end_of_service_date && properties.end_of_service_date !== "%END_OF_SERVICE_DATE%"  && properties.end_of_service_email && properties.end_of_service_email !== "%END_OF_SERVICE_EMAIL%"}>
        {() => (
          <EndOfService tokenExist={tokenExist}/>
        )}
      </When>
      <AppHeader 
          items={allBreadcrumbs}
          properties={properties}
      />
      <ErrorBoundary>
        <If condition={isPublic}>
          <Then>
            {() => (
              <PublicRouting/>
            )}
          </Then>
          <Else>
            {() => (
              <If condition={isRefreshing}>
                <Then>
                  {() => <Fetching isFetching/>}            
                </Then>
                <Else>
                  {() => (
                    <Fragment>
                      <When condition={tokenExist}>
                        {() => (
                          <If condition={isMobile}>
                            <Then>
                              {() => <MobileRouting/>}
                            </Then>
                            <Else>
                              {() => <DesktopRouting isIframe={isIframe}/>}
                            </Else>
                          </If>
                        )}
                      </When>
                      <NonAuthedRouting 
                          isMcpLogin={isMcpLogin} 
                          tokenExist={tokenExist}
                      />
                    </Fragment>
                  )}
                </Else>
              </If>
            )}
          </Else>
        </If>
      </ErrorBoundary>
    </Suspense>
  )
}

export default memo(App);
